import { hsalsa, xsalsa20poly1305 } from '@noble/ciphers/salsa.js';

import { CouldNotDecryptError } from '../errors';
import {
  OpaqueX25519Key,
  X25519_KEY_LENGTH,
  exportX25519PrivKey,
  exportX25519PubKey,
  exportX25519PubKeyBytes,
  importX25519PrivKey,
  importX25519PubKey,
  newX25519KeyPair,
  x25519DeriveShared,
} from '../internal/x25519';
import { SALT_ENCRYPTION } from '../salts';
import { Brand } from '../types';
import { ArrayOrBase64, concatArrays, deriveSecretFromSeed, loadArrayOrBase64 } from '../utils';

export type RawEncryptionPrivateKey = Brand<Uint8Array<ArrayBuffer>, 'RawEncryptionPrivateKey'>;
export type RawEncryptionPublicKey = Brand<Uint8Array<ArrayBuffer>, 'RawEncryptionPublicKey'>;

const NONCE_LENGTH = 24;

export function loadRawEncryptionPublicKey(data: ArrayOrBase64): RawEncryptionPublicKey {
  return loadArrayOrBase64(data, X25519_KEY_LENGTH) as RawEncryptionPublicKey;
}

export function loadRawEncryptionPrivateKey(data: ArrayOrBase64): RawEncryptionPrivateKey {
  return loadArrayOrBase64(data, X25519_KEY_LENGTH) as RawEncryptionPrivateKey;
}

export type EncryptionPublicKey = Brand<OpaqueX25519Key, 'EncryptionPublicKey'>;
export type EncryptionPrivateKey = Brand<OpaqueX25519Key, 'EncryptionPrivateKey'>;

export type AsymEncryptedBlob = Brand<Uint8Array<ArrayBuffer>, 'AsymEncryptedBlob'>;

export interface EncryptionKeyPair {
  encPrivKey: EncryptionPrivateKey;
  encPubKey: EncryptionPublicKey;
}

// Salsa20 sigma constant, "expand 32-byte k" decoded as Uint32Array
// see https://github.com/jedisct1/libsodium/blob/cb20f6d9cd5221ad988ed45a700685c3791757f0/src/libsodium/crypto_core/hsalsa20/ref2/core_hsalsa20_ref2.c#L27-L30
const SIGMA = new Uint32Array([0x61707865, 0x3320646e, 0x79622d32, 0x6b206574]);

export async function loadEncryptionPublicKey(data: ArrayOrBase64): Promise<EncryptionPublicKey> {
  const raw = loadArrayOrBase64(data, X25519_KEY_LENGTH);
  return (await importX25519PubKey(raw)) as EncryptionPublicKey;
}

export async function loadEncryptionPrivateKey(data: ArrayOrBase64): Promise<EncryptionPrivateKey> {
  const raw = loadArrayOrBase64(data, X25519_KEY_LENGTH);
  return (await importX25519PrivKey(raw)) as EncryptionPrivateKey;
}

export async function serializeEncryptionPublicKey(
  epk: EncryptionPublicKey,
): Promise<RawEncryptionPublicKey> {
  const raw = await exportX25519PubKey(epk);
  return loadRawEncryptionPublicKey(raw);
}

export async function serializeEncryptionPrivateKey(
  epk: EncryptionPrivateKey,
): Promise<RawEncryptionPrivateKey> {
  const raw = await exportX25519PrivKey(epk);
  return loadRawEncryptionPrivateKey(raw);
}

export async function deriveEncryptionKeyPair(
  seed: Uint8Array<ArrayBuffer>,
  secondarySeed: Uint8Array<ArrayBuffer> | null,
  salt: string,
): Promise<EncryptionKeyPair> {
  const secret = await deriveSecretFromSeed(
    seed,
    secondarySeed,
    SALT_ENCRYPTION + salt,
    X25519_KEY_LENGTH,
  );
  const encPrivKey = await loadEncryptionPrivateKey(secret);
  const encPubKey = await deriveEncryptionPublicKey(encPrivKey);
  return { encPrivKey, encPubKey };
}

export async function deriveEncryptionPublicKey(
  encPrivKey: EncryptionPrivateKey,
): Promise<EncryptionPublicKey> {
  const rawPublic = await exportX25519PubKeyBytes(encPrivKey);
  return loadEncryptionPublicKey(rawPublic);
}

export function loadAsymEncryptedBlob(data: ArrayOrBase64): AsymEncryptedBlob {
  return loadArrayOrBase64(data) as AsymEncryptedBlob;
}

/**
 * Replicate NaCl's crypto_box_beforenm: X25519 shared point → HSalsa20(shared, zeros) → box key.
 * This is the key derivation step that makes NaCl box wire-compatible.
 * See https://libsodium.gitbook.io/doc/public-key_cryptography/authenticated_encryption#precalculation-interface
 * https://github.com/jedisct1/libsodium/blob/cb20f6d9cd5221ad988ed45a700685c3791757f0/src/libsodium/crypto_box/crypto_box.c#L78
 */
function deriveBoxKey(sharedPoint: Uint8Array): Uint8Array {
  if (sharedPoint.byteLength !== 32) {
    throw new Error(
      `deriveBoxKey must be called with 32 byte sharedPoint argument ${sharedPoint.byteLength}`,
    );
  }
  // hsalsa works with uint32 arrays, so convert accordingly. we can assume this is little endian
  const k = new Uint32Array(sharedPoint.buffer, sharedPoint.byteOffset, 8);
  const zeros = new Uint32Array(4);
  const out = new Uint32Array(8);
  hsalsa(SIGMA, k, zeros, out);
  return new Uint8Array(out.buffer);
}

export async function newEncryptionKeyPair(): Promise<EncryptionKeyPair> {
  const { privateKey, publicKey } = await newX25519KeyPair();
  return {
    encPrivKey: privateKey as EncryptionPrivateKey,
    encPubKey: publicKey as EncryptionPublicKey,
  };
}

/**
 * Asymmetric encryption using Web Crypto X25519 + @noble/ciphers XSalsa20-Poly1305.
 * Wire-compatible with tweetnacl's nacl.box (same output format: nonce || ephPub || ciphertext).
 */
export async function asymEncrypt(
  encPubKey: EncryptionPublicKey,
  data: Uint8Array<ArrayBuffer>,
): Promise<AsymEncryptedBlob> {
  const { privateKey: ephPriv, publicKey: ephPub } = await newX25519KeyPair();

  const sharedPoint = await x25519DeriveShared(ephPriv, encPubKey);
  const boxKey = deriveBoxKey(sharedPoint);

  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LENGTH));
  const ciphertext = xsalsa20poly1305(boxKey, nonce).encrypt(data);

  const ephPubRaw = await exportX25519PubKey(ephPub);
  return loadAsymEncryptedBlob(concatArrays(nonce, ephPubRaw, ciphertext));
}

/**
 * Asymmetric decryption using Web Crypto X25519 + @noble/ciphers XSalsa20-Poly1305.
 * Wire-compatible with tweetnacl's nacl.box.open.
 */
export async function asymDecrypt(
  encPrivKey: EncryptionPrivateKey,
  encryptedBlob: AsymEncryptedBlob,
): Promise<Uint8Array<ArrayBuffer>> {
  const nonce = encryptedBlob.slice(0, NONCE_LENGTH);
  const ephPubRaw = encryptedBlob.slice(NONCE_LENGTH, NONCE_LENGTH + X25519_KEY_LENGTH);
  const ciphertext = encryptedBlob.slice(NONCE_LENGTH + X25519_KEY_LENGTH);

  const ephPublicKey = await loadEncryptionPublicKey(ephPubRaw);

  const sharedPoint = await x25519DeriveShared(encPrivKey, ephPublicKey);
  const boxKey = deriveBoxKey(sharedPoint);

  try {
    return xsalsa20poly1305(boxKey, nonce).decrypt(ciphertext);
  } catch {
    throw new CouldNotDecryptError();
  }
}
